import { ArtboardCountBadge } from '@repo/editor/src/components/ArtboardCountBadge/ArtboardCountBadge';
import { withEventCatcher } from '@repo/event-processor';
import { H4, P4 } from '@repo/ui/components/Typography';
import { QUERIES } from '@repo/ui/queries';
import { themeStyle } from '@repo/ui/theming';
import { themeStyle as newThemeStyle } from '@repo/ui-kit/themeStyle';
import styled, { css, keyframes } from 'styled-components';

export const StyledArtboardCountBadge = styled(ArtboardCountBadge).withConfig({
  shouldForwardProp: () => true,
})`
  position: absolute;
  bottom: 16px;
  right: 16px;
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const animationRule = css`
  ${fadeIn};
`;

export const Image = styled.img<{
  height?: number;
  width?: number;
}>`
  display: block;
  user-drag: none;
  width: ${({ width }) => (width ? `${width}px` : 'unset')};
  max-width: 100%;
  box-sizing: border-box;
  object-fit: contain;
  height: ${({ height }) => (height ? `${height}px` : '100%')};
`;

export const TopButton = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;

  & > * {
    margin-left: 8px;
  }

  @media (pointer: fine) {
    visibility: hidden;
  }
`;

export const TemplateInfo = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 100%;
  height: 50%;
  padding: 16px;
  background: transparent;
  transition: background 250ms;
`;

export const InfoOnHover = styled.div`
  opacity: 0;
  transition: opacity 250ms;
  overflow: hidden;
`;

export const BorderBox = styled.div`
  position: absolute;
  pointer-events: none;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  border-radius: ${themeStyle.radiusMedium};
  border: 1px solid ${themeStyle.varTransparentBlack};
`;

export const ImageWrapper = styled.div<{ background: string }>`
  position: relative;
  overflow: hidden;
  border-radius: ${themeStyle.radiusLarge};
  display: flex;
  justify-content: center;
  background-color: ${({ background }) => background};

  &:hover {
    ${StyledArtboardCountBadge} {
      visibility: hidden;
    }

    ${TopButton} {
      visibility: visible;
    }

    ${TemplateInfo} {
      background: linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.64) 0%,
        rgba(151, 151, 151, 0) 100%
      );
    }

    ${InfoOnHover} {
      opacity: 1;
    }
  }
`;

// the premium icon has a shadow implemented in the svg.
// To align the actual icon correctly we need a negative margin.
export const PremiumIconWrapper = styled.div`
  margin: 0 -3px;
`;

export const BookmarkContainer = styled.div<{ activeDefaultColor: string }>`
  position: absolute;
  top: -1px;
  left: 16px;
  filter: drop-shadow(0 2px 1px rgba(0, 0, 0, 0.5));

  ${ImageWrapper}:hover & .fillDefault:not(.secondary) {
    fill: ${({ activeDefaultColor }) => activeDefaultColor};
  }
`;

export const ChallengeContainer = styled.div<{
  extraBottomSpace: boolean | null;
}>`
  position: absolute;
  top: ${({ extraBottomSpace }) => (extraBottomSpace ? '28px' : '16px')};
  left: 14px;
  filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.5));
`;

export const Wrapper = styled.div`
  box-sizing: border-box;

  display: flex;
  flex-direction: column;

  overflow: hidden;
  cursor: pointer;

  animation: ${animationRule};
  animation-duration: 1s;
`;

export const AnchorWrapper = styled.a`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  animation: ${animationRule};
  animation-duration: 1s;
  border: 1px solid ${newThemeStyle.colorBorder};
  border-radius: ${themeStyle.radiusLarge};
`;

export const AnchorWrapperWithEventCatcher = withEventCatcher(AnchorWrapper, {
  componentCategory: 'module',
  shouldTrack: {
    showScrolled: true,
  },
});

export const BottomRow = styled.div`
  margin: 8px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const FlexGroup = styled.div`
  display: flex;
  align-items: center;
`;

export const PostTitle = styled(H4)`
  margin-top: 3px;
`;

export const CategoryLabel = styled(P4)`
  line-height: 1;
`;

export const LikesAndPopularityFlexGroup = styled(FlexGroup)<{
  areLikesAndPopularityHiddenOnMobile?: boolean;
}>`
  display: ${({ areLikesAndPopularityHiddenOnMobile }) =>
    areLikesAndPopularityHiddenOnMobile ? 'none' : 'flex'};

  ${QUERIES.largeTabletAndUp} {
    display: flex;
  }
`;
